/**
 * Inter-MDA Records Transfer Protocol Service
 *
 * Manages secure transfer of personnel records between MDAs through
 * the OHCS central hub with complete audit trail and verification.
 */

export interface TransferRequest {
  id: string;
  requestNumber: string;
  type: TransferType;
  status: TransferStatus;
  priority: 'urgent' | 'normal' | 'low';

  // Record details
  pfileId: string;
  fileNumber: string;
  staffId: string;
  staffName: string;
  currentGrade: string;

  // Transfer parties
  sourceMDA: MDAInfo;
  destinationMDA: MDAInfo;
  ohcsHub: HubInfo;

  // Request details
  reason: TransferReason;
  effectiveDate: Date;
  notes?: string;
  attachments: TransferAttachment[];

  // Tracking
  requestedBy: string;
  requestedAt: Date;
  dispatchedAt?: Date;
  receivedAt?: Date;
  completedAt?: Date;

  // Verification
  verificationStatus: VerificationStatus;
  verifications: TransferVerification[];

  // Timeline
  timeline: TransferEvent[];

  // Rollback
  canRollback: boolean;
  rollbackDeadline?: Date;
}

export type TransferType =
  | 'permanent_transfer'     // Full transfer to new MDA
  | 'secondment'             // Temporary posting
  | 'temporary_assignment'   // Short-term assignment
  | 'loan'                   // Inter-MDA loan
  | 'return_from_secondment' // Return to substantive MDA
  | 'consolidation';         // Merge records from multiple MDAs

export type TransferStatus =
  | 'draft'              // Being prepared
  | 'pending_approval'   // Awaiting source MDA approval
  | 'approved'           // Source MDA approved
  | 'dispatched'         // Sent to OHCS Hub
  | 'hub_processing'     // OHCS verifying
  | 'hub_verified'       // OHCS approved
  | 'in_transit'         // En route to destination
  | 'pending_receipt'    // Awaiting destination acknowledgment
  | 'received'           // Destination confirmed receipt
  | 'completed'          // Transfer finalized
  | 'rejected'           // Transfer rejected
  | 'rolled_back'        // Transfer reversed
  | 'cancelled';         // Cancelled before completion

export type TransferReason =
  | 'promotion_transfer'
  | 'posting_transfer'
  | 'secondment'
  | 'request_transfer'
  | 'disciplinary_transfer'
  | 'rationalization'
  | 'consolidation'
  | 'retirement_transfer'
  | 'other';

export interface MDAInfo {
  code: string;
  name: string;
  registryContact: {
    name: string;
    email: string;
    phone: string;
  };
  address: string;
}

export interface HubInfo {
  name: string;
  processingOfficer?: string;
  verificationOfficer?: string;
  processedAt?: Date;
  verifiedAt?: Date;
}

export interface TransferAttachment {
  id: string;
  name: string;
  type: 'posting_letter' | 'approval_memo' | 'acceptance_letter' | 'other';
  fileUrl: string;
  uploadedBy: string;
  uploadedAt: Date;
}

export type VerificationStatus =
  | 'pending'
  | 'in_progress'
  | 'verified'
  | 'failed'
  | 'partial';

export interface TransferVerification {
  id: string;
  type: VerificationType;
  status: 'pending' | 'passed' | 'failed' | 'skipped';
  verifiedBy?: string;
  verifiedAt?: Date;
  notes?: string;
  issues?: string[];
}

export type VerificationType =
  | 'identity_verification'     // Staff identity matches
  | 'record_completeness'       // All required documents present
  | 'authorization_check'       // Proper approvals in place
  | 'file_number_validation'    // File number format correct
  | 'service_history_check'     // Service history consistent
  | 'outstanding_issues'        // No unresolved disciplinary/audit issues
  | 'salary_arrears_check'      // No outstanding salary issues
  | 'leave_balance_transfer'    // Leave balances reconciled
  | 'clearance_verification';   // Proper clearance obtained

export interface TransferEvent {
  id: string;
  type: TransferEventType;
  timestamp: Date;
  actor: string;
  actorRole: string;
  description: string;
  metadata?: Record<string, unknown>;
}

export type TransferEventType =
  | 'request_created'
  | 'request_submitted'
  | 'approval_granted'
  | 'approval_denied'
  | 'dispatched'
  | 'hub_received'
  | 'verification_started'
  | 'verification_completed'
  | 'verification_failed'
  | 'forwarded_to_destination'
  | 'destination_received'
  | 'destination_acknowledged'
  | 'transfer_completed'
  | 'transfer_rejected'
  | 'rollback_initiated'
  | 'rollback_completed'
  | 'comment_added'
  | 'attachment_added';

export interface TransferStatistics {
  period: { start: Date; end: Date };
  totalTransfers: number;
  byStatus: Record<TransferStatus, number>;
  byType: Record<TransferType, number>;
  byReason: Record<TransferReason, number>;
  averageProcessingDays: number;
  averageVerificationDays: number;
  successRate: number;
  rejectionRate: number;
  topSourceMDAs: Array<{ mda: string; count: number }>;
  topDestinationMDAs: Array<{ mda: string; count: number }>;
  pendingTransfers: number;
  overdueTransfers: number;
}

class TransferProtocolService {
  /**
   * Generate transfer request number
   */
  private generateRequestNumber(): string {
    const year = new Date().getFullYear();
    const sequence = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
    return `TRF-${year}-${sequence}`;
  }

  /**
   * Create a new transfer request
   */
  createTransferRequest(params: {
    type: TransferType;
    priority: 'urgent' | 'normal' | 'low';
    pfileId: string;
    fileNumber: string;
    staffId: string;
    staffName: string;
    currentGrade: string;
    sourceMDA: MDAInfo;
    destinationMDA: MDAInfo;
    reason: TransferReason;
    effectiveDate: Date;
    requestedBy: string;
    notes?: string;
  }): TransferRequest {
    const now = new Date();
    const id = `transfer_${Date.now()}`;

    return {
      id,
      requestNumber: this.generateRequestNumber(),
      type: params.type,
      status: 'draft',
      priority: params.priority,
      pfileId: params.pfileId,
      fileNumber: params.fileNumber,
      staffId: params.staffId,
      staffName: params.staffName,
      currentGrade: params.currentGrade,
      sourceMDA: params.sourceMDA,
      destinationMDA: params.destinationMDA,
      ohcsHub: {
        name: 'OHCS Central Records Hub',
      },
      reason: params.reason,
      effectiveDate: params.effectiveDate,
      notes: params.notes,
      attachments: [],
      requestedBy: params.requestedBy,
      requestedAt: now,
      verificationStatus: 'pending',
      verifications: this.initializeVerifications(),
      timeline: [{
        id: `event_${Date.now()}`,
        type: 'request_created',
        timestamp: now,
        actor: params.requestedBy,
        actorRole: 'Registry Officer',
        description: `Transfer request created for ${params.staffName}`,
      }],
      canRollback: false,
    };
  }

  /**
   * Initialize verification checklist
   */
  private initializeVerifications(): TransferVerification[] {
    const verificationTypes: VerificationType[] = [
      'identity_verification',
      'record_completeness',
      'authorization_check',
      'file_number_validation',
      'service_history_check',
      'outstanding_issues',
      'salary_arrears_check',
      'leave_balance_transfer',
      'clearance_verification',
    ];

    return verificationTypes.map(type => ({
      id: `verification_${type}_${Date.now()}`,
      type,
      status: 'pending' as const,
    }));
  }

  /**
   * Submit transfer request for approval
   */
  submitForApproval(
    request: TransferRequest,
    submittedBy: string
  ): TransferRequest {
    return {
      ...request,
      status: 'pending_approval',
      timeline: [
        ...request.timeline,
        {
          id: `event_${Date.now()}`,
          type: 'request_submitted',
          timestamp: new Date(),
          actor: submittedBy,
          actorRole: 'Registry Officer',
          description: 'Transfer request submitted for approval',
        },
      ],
    };
  }

  /**
   * Approve transfer at source MDA
   */
  approveAtSource(
    request: TransferRequest,
    approvedBy: string,
    notes?: string
  ): TransferRequest {
    return {
      ...request,
      status: 'approved',
      timeline: [
        ...request.timeline,
        {
          id: `event_${Date.now()}`,
          type: 'approval_granted',
          timestamp: new Date(),
          actor: approvedBy,
          actorRole: 'Source MDA HR Director',
          description: `Transfer approved by ${request.sourceMDA.name}`,
          metadata: { notes },
        },
      ],
    };
  }

  /**
   * Dispatch to OHCS Hub
   */
  dispatchToHub(
    request: TransferRequest,
    dispatchedBy: string
  ): TransferRequest {
    const now = new Date();
    return {
      ...request,
      status: 'dispatched',
      dispatchedAt: now,
      timeline: [
        ...request.timeline,
        {
          id: `event_${Date.now()}`,
          type: 'dispatched',
          timestamp: now,
          actor: dispatchedBy,
          actorRole: 'Registry Officer',
          description: 'Transfer dispatched to OHCS Central Hub',
        },
      ],
    };
  }

  /**
   * Process at OHCS Hub
   */
  receiveAtHub(
    request: TransferRequest,
    processingOfficer: string
  ): TransferRequest {
    return {
      ...request,
      status: 'hub_processing',
      ohcsHub: {
        ...request.ohcsHub,
        processingOfficer,
        processedAt: new Date(),
      },
      verificationStatus: 'in_progress',
      timeline: [
        ...request.timeline,
        {
          id: `event_${Date.now()}`,
          type: 'hub_received',
          timestamp: new Date(),
          actor: processingOfficer,
          actorRole: 'OHCS Processing Officer',
          description: 'Transfer received at OHCS Hub for verification',
        },
        {
          id: `event_${Date.now() + 1}`,
          type: 'verification_started',
          timestamp: new Date(),
          actor: processingOfficer,
          actorRole: 'OHCS Processing Officer',
          description: 'Verification process initiated',
        },
      ],
    };
  }

  /**
   * Perform verification
   */
  performVerification(
    request: TransferRequest,
    verificationType: VerificationType,
    status: 'passed' | 'failed' | 'skipped',
    verifiedBy: string,
    notes?: string,
    issues?: string[]
  ): TransferRequest {
    const updatedVerifications = request.verifications.map(v =>
      v.type === verificationType
        ? { ...v, status, verifiedBy, verifiedAt: new Date(), notes, issues }
        : v
    );

    // Determine overall verification status
    const allVerified = updatedVerifications.every(
      v => v.status === 'passed' || v.status === 'skipped'
    );
    const anyFailed = updatedVerifications.some(v => v.status === 'failed');
    const allComplete = updatedVerifications.every(v => v.status !== 'pending');

    let verificationStatus: VerificationStatus = 'in_progress';
    if (allComplete) {
      verificationStatus = anyFailed ? 'failed' : allVerified ? 'verified' : 'partial';
    }

    return {
      ...request,
      verifications: updatedVerifications,
      verificationStatus,
    };
  }

  /**
   * Complete hub verification
   */
  completeHubVerification(
    request: TransferRequest,
    verificationOfficer: string
  ): TransferRequest {
    if (request.verificationStatus !== 'verified') {
      throw new Error('Cannot complete verification - not all checks passed');
    }

    return {
      ...request,
      status: 'hub_verified',
      ohcsHub: {
        ...request.ohcsHub,
        verificationOfficer,
        verifiedAt: new Date(),
      },
      timeline: [
        ...request.timeline,
        {
          id: `event_${Date.now()}`,
          type: 'verification_completed',
          timestamp: new Date(),
          actor: verificationOfficer,
          actorRole: 'OHCS Verification Officer',
          description: 'All verifications completed successfully',
        },
      ],
    };
  }

  /**
   * Forward to destination MDA
   */
  forwardToDestination(
    request: TransferRequest,
    forwardedBy: string
  ): TransferRequest {
    return {
      ...request,
      status: 'in_transit',
      timeline: [
        ...request.timeline,
        {
          id: `event_${Date.now()}`,
          type: 'forwarded_to_destination',
          timestamp: new Date(),
          actor: forwardedBy,
          actorRole: 'OHCS Processing Officer',
          description: `Transfer forwarded to ${request.destinationMDA.name}`,
        },
      ],
    };
  }

  /**
   * Receive at destination MDA
   */
  receiveAtDestination(
    request: TransferRequest,
    receivedBy: string
  ): TransferRequest {
    const now = new Date();
    return {
      ...request,
      status: 'received',
      receivedAt: now,
      timeline: [
        ...request.timeline,
        {
          id: `event_${Date.now()}`,
          type: 'destination_received',
          timestamp: now,
          actor: receivedBy,
          actorRole: 'Destination Registry Officer',
          description: `Transfer received at ${request.destinationMDA.name}`,
        },
      ],
    };
  }

  /**
   * Acknowledge and complete transfer
   */
  acknowledgeTransfer(
    request: TransferRequest,
    acknowledgedBy: string,
    newFileNumber?: string
  ): TransferRequest {
    const now = new Date();
    const rollbackDeadline = new Date(now);
    rollbackDeadline.setDate(rollbackDeadline.getDate() + 30); // 30-day rollback window

    return {
      ...request,
      status: 'completed',
      completedAt: now,
      fileNumber: newFileNumber || request.fileNumber,
      canRollback: true,
      rollbackDeadline,
      timeline: [
        ...request.timeline,
        {
          id: `event_${Date.now()}`,
          type: 'destination_acknowledged',
          timestamp: now,
          actor: acknowledgedBy,
          actorRole: 'Destination HR Director',
          description: 'Transfer acknowledged by destination MDA',
          metadata: newFileNumber ? { newFileNumber } : undefined,
        },
        {
          id: `event_${Date.now() + 1}`,
          type: 'transfer_completed',
          timestamp: now,
          actor: 'System',
          actorRole: 'System',
          description: 'Transfer completed successfully',
        },
      ],
    };
  }

  /**
   * Reject transfer
   */
  rejectTransfer(
    request: TransferRequest,
    rejectedBy: string,
    reason: string,
    stage: 'source' | 'hub' | 'destination'
  ): TransferRequest {
    const stageRoles: Record<string, string> = {
      source: 'Source MDA HR Director',
      hub: 'OHCS Verification Officer',
      destination: 'Destination HR Director',
    };

    return {
      ...request,
      status: 'rejected',
      timeline: [
        ...request.timeline,
        {
          id: `event_${Date.now()}`,
          type: 'transfer_rejected',
          timestamp: new Date(),
          actor: rejectedBy,
          actorRole: stageRoles[stage],
          description: `Transfer rejected: ${reason}`,
          metadata: { stage, reason },
        },
      ],
    };
  }

  /**
   * Initiate rollback
   */
  initiateRollback(
    request: TransferRequest,
    initiatedBy: string,
    reason: string
  ): TransferRequest {
    if (!request.canRollback) {
      throw new Error('Transfer cannot be rolled back');
    }

    if (request.rollbackDeadline && new Date() > request.rollbackDeadline) {
      throw new Error('Rollback deadline has passed');
    }

    return {
      ...request,
      status: 'rolled_back',
      canRollback: false,
      timeline: [
        ...request.timeline,
        {
          id: `event_${Date.now()}`,
          type: 'rollback_initiated',
          timestamp: new Date(),
          actor: initiatedBy,
          actorRole: 'HR Director',
          description: `Rollback initiated: ${reason}`,
        },
        {
          id: `event_${Date.now() + 1}`,
          type: 'rollback_completed',
          timestamp: new Date(),
          actor: 'System',
          actorRole: 'System',
          description: 'Transfer rolled back - records returned to source MDA',
        },
      ],
    };
  }

  /**
   * Calculate transfer statistics
   */
  calculateStatistics(
    transfers: TransferRequest[],
    startDate: Date,
    endDate: Date
  ): TransferStatistics {
    const periodTransfers = transfers.filter(t => {
      const date = new Date(t.requestedAt);
      return date >= startDate && date <= endDate;
    });

    // Initialize counts
    const byStatus: Record<TransferStatus, number> = {
      draft: 0,
      pending_approval: 0,
      approved: 0,
      dispatched: 0,
      hub_processing: 0,
      hub_verified: 0,
      in_transit: 0,
      pending_receipt: 0,
      received: 0,
      completed: 0,
      rejected: 0,
      rolled_back: 0,
      cancelled: 0,
    };

    const byType: Record<TransferType, number> = {
      permanent_transfer: 0,
      secondment: 0,
      temporary_assignment: 0,
      loan: 0,
      return_from_secondment: 0,
      consolidation: 0,
    };

    const byReason: Record<TransferReason, number> = {
      promotion_transfer: 0,
      posting_transfer: 0,
      secondment: 0,
      request_transfer: 0,
      disciplinary_transfer: 0,
      rationalization: 0,
      consolidation: 0,
      retirement_transfer: 0,
      other: 0,
    };

    const sourceMDACounts = new Map<string, number>();
    const destMDACounts = new Map<string, number>();
    const processingDays: number[] = [];
    const verificationDays: number[] = [];

    periodTransfers.forEach(t => {
      byStatus[t.status]++;
      byType[t.type]++;
      byReason[t.reason]++;

      sourceMDACounts.set(
        t.sourceMDA.name,
        (sourceMDACounts.get(t.sourceMDA.name) || 0) + 1
      );
      destMDACounts.set(
        t.destinationMDA.name,
        (destMDACounts.get(t.destinationMDA.name) || 0) + 1
      );

      // Calculate processing time
      if (t.completedAt) {
        const days = (new Date(t.completedAt).getTime() - new Date(t.requestedAt).getTime())
          / (1000 * 60 * 60 * 24);
        processingDays.push(days);
      }

      // Calculate verification time
      if (t.ohcsHub.verifiedAt && t.ohcsHub.processedAt) {
        const days = (new Date(t.ohcsHub.verifiedAt).getTime() - new Date(t.ohcsHub.processedAt).getTime())
          / (1000 * 60 * 60 * 24);
        verificationDays.push(days);
      }
    });

    const completedCount = byStatus.completed;
    const rejectedCount = byStatus.rejected;
    const totalDecided = completedCount + rejectedCount;

    // Calculate overdue (pending > 14 days)
    const now = new Date();
    const overdueTransfers = periodTransfers.filter(t => {
      if (t.status === 'completed' || t.status === 'rejected' || t.status === 'cancelled') {
        return false;
      }
      const daysPending = (now.getTime() - new Date(t.requestedAt).getTime()) / (1000 * 60 * 60 * 24);
      return daysPending > 14;
    }).length;

    return {
      period: { start: startDate, end: endDate },
      totalTransfers: periodTransfers.length,
      byStatus,
      byType,
      byReason,
      averageProcessingDays: processingDays.length > 0
        ? Math.round(processingDays.reduce((a, b) => a + b, 0) / processingDays.length * 10) / 10
        : 0,
      averageVerificationDays: verificationDays.length > 0
        ? Math.round(verificationDays.reduce((a, b) => a + b, 0) / verificationDays.length * 10) / 10
        : 0,
      successRate: totalDecided > 0 ? Math.round((completedCount / totalDecided) * 100) : 0,
      rejectionRate: totalDecided > 0 ? Math.round((rejectedCount / totalDecided) * 100) : 0,
      topSourceMDAs: Array.from(sourceMDACounts.entries())
        .map(([mda, count]) => ({ mda, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10),
      topDestinationMDAs: Array.from(destMDACounts.entries())
        .map(([mda, count]) => ({ mda, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10),
      pendingTransfers: periodTransfers.filter(t =>
        !['completed', 'rejected', 'cancelled', 'rolled_back'].includes(t.status)
      ).length,
      overdueTransfers,
    };
  }

  /**
   * Get status label
   */
  getStatusLabel(status: TransferStatus): string {
    const labels: Record<TransferStatus, string> = {
      draft: 'Draft',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      dispatched: 'Dispatched',
      hub_processing: 'Hub Processing',
      hub_verified: 'Hub Verified',
      in_transit: 'In Transit',
      pending_receipt: 'Pending Receipt',
      received: 'Received',
      completed: 'Completed',
      rejected: 'Rejected',
      rolled_back: 'Rolled Back',
      cancelled: 'Cancelled',
    };
    return labels[status];
  }

  /**
   * Get transfer type label
   */
  getTypeLabel(type: TransferType): string {
    const labels: Record<TransferType, string> = {
      permanent_transfer: 'Permanent Transfer',
      secondment: 'Secondment',
      temporary_assignment: 'Temporary Assignment',
      loan: 'Inter-MDA Loan',
      return_from_secondment: 'Return from Secondment',
      consolidation: 'Record Consolidation',
    };
    return labels[type];
  }

  /**
   * Get reason label
   */
  getReasonLabel(reason: TransferReason): string {
    const labels: Record<TransferReason, string> = {
      promotion_transfer: 'Promotion Transfer',
      posting_transfer: 'Posting Transfer',
      secondment: 'Secondment',
      request_transfer: 'Staff Request',
      disciplinary_transfer: 'Disciplinary Transfer',
      rationalization: 'Rationalization',
      consolidation: 'Record Consolidation',
      retirement_transfer: 'Retirement Transfer',
      other: 'Other',
    };
    return labels[reason];
  }
}

// Export singleton instance
export const transferProtocol = new TransferProtocolService();

export type { TransferProtocolService };
